import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { isChunkLoadError, reloadOnceForStaleChunk } from './chunkReload'
import { useHealthStore } from '@/stores/healthStore'

/**
 * `React.lazy` that survives a deploy AND a dead signal.
 *
 * - Stale deploy (the chunk 404s while the network works): reload once to pick
 *   up the new build, as before.
 * - Offline (UX-052): NEVER reload. A reload with no network replaces the whole
 *   app with the browser's "No internet" page. Instead the import stays
 *   pending — the route's Suspense fallback (<RouteFallback/>) reads the
 *   network state and says "You're offline — this page hasn't been downloaded
 *   yet" — and is retried automatically when the connection comes back.
 *
 * "Offline" is decided by asking: can we fetch the app's own index.html? If
 * not, it's the network, not a deploy.
 */

const RETRY_INTERVAL_MS = 5_000

async function canReachApp(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false
  try {
    const controller = new AbortController()
    const t = setTimeout(() => controller.abort(), 5_000)
    const res = await fetch('/', { method: 'HEAD', cache: 'no-store', signal: controller.signal })
    clearTimeout(t)
    return res.ok || res.status < 500
  } catch {
    return false
  }
}

/** Resolves once the browser reports online and the app's origin answers. */
function waitUntilReachable(): Promise<void> {
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const check = async () => {
      if (await canReachApp()) {
        window.removeEventListener('online', onOnline)
        if (timer) clearTimeout(timer)
        resolve()
      } else {
        timer = setTimeout(check, RETRY_INTERVAL_MS)
      }
    }
    const onOnline = () => {
      if (timer) clearTimeout(timer)
      void check()
    }
    window.addEventListener('online', onOnline)
    timer = setTimeout(check, RETRY_INTERVAL_MS)
  })
}

export async function loadChunkWithRetry<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory()
  } catch (error) {
    if (!isChunkLoadError(error)) throw error
    if (!(await canReachApp())) {
      // Offline: tell the shell, wait for the network, then try again.
      useHealthStore.getState().setBrowserOnline(false)
      await waitUntilReachable()
      try {
        return await factory()
      } catch (retryError) {
        // Some browsers cache a failed module import; we're online now, so a
        // reload is safe and fetches it fresh.
        if (isChunkLoadError(retryError) && reloadOnceForStaleChunk()) {
          return new Promise<T>(() => undefined)
        }
        throw retryError
      }
    }
    if (reloadOnceForStaleChunk()) {
      // Page is reloading; keep Suspense pending instead of flashing an error.
      return new Promise<T>(() => undefined)
    }
    throw error
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PreloadableLazy<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  /** Start downloading the chunk now (e.g. after sign-in) so it opens offline. */
  preload: () => Promise<unknown>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): PreloadableLazy<T> {
  let pending: Promise<{ default: T }> | null = null
  const load = () => {
    if (!pending) {
      pending = loadChunkWithRetry(factory).catch((e) => {
        pending = null
        throw e
      })
    }
    return pending
  }
  const component = lazy(load) as PreloadableLazy<T>
  component.preload = () => load().catch(() => undefined)
  return component
}
